import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers } from "vue"

import { ref, onMounted, watch } from 'vue';
import RichTextEditor from '@/components/RichTextEditor/RichTextEditor.vue';
import ImageCropper from '@/components/ImageCropper/ImageCropper.vue';
import type { Founder } from '@/services/drUniversity/founders/founder.model';
import { FounderCardProps } from '@/components/DrUniversity/FounderCard/FounderCard.model';
import { Wc3Races } from '@/services/wc3/wc3.model';
import { SocialPlatform } from '@/services/common/common.model';
import { getClassicWc3Races } from '@/services/wc3/wc3.service';
import { getSocialPlatforms, uploadImage } from '@/services/common/common.service';
import { compressImage } from '@/utils/image.util';

interface Props {
  initialData?: FounderCardProps;
  onSubmit: (payload: Founder) => Promise<void>;
  loading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FounderForm',
  props: {
    initialData: {},
    onSubmit: { type: Function },
    loading: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const name = ref('');
const role = ref('');
const contribution = ref('');
const embeddedImages = ref<File[]>([]);

const image = ref<string | null>(null);
const imageFile = ref<File | null>(null);
const selectedRaces = ref<number[]>([]);
const socialLinks = ref<Record<string, string>>({});

const races = ref<Wc3Races[]>([]);
const socialPlatforms = ref<SocialPlatform[]>([]);

const pageLoading = ref(true);
const errorMessage = ref('');

function fillFormFromInitialData(data: FounderCardProps) {
  name.value = data.name;
  role.value = data.role;
  contribution.value = data.contribution;
  image.value = data.image;

  const raceMap = { human: 1, orc: 2, nightelf: 3, undead: 4 };
  selectedRaces.value = Object.entries(data.race)
    .filter(([_, v]) => v)
    .map(([key]) => raceMap[key as keyof typeof raceMap]);

  socialLinks.value = {};
  for (const key of ['tiktok', 'youtube', 'liquipedia', 'soop', 'twitch', 'instagram', 'twitter', 'reddit', 'w3champions']) {
    if ((data as any)[key]) socialLinks.value[key] = (data as any)[key];
  }
}

onMounted(async () => {
  try {
    const [racesRes, socialRes] = await Promise.all([getClassicWc3Races(), getSocialPlatforms()]);
    races.value = racesRes ?? [];
    socialPlatforms.value = socialRes ?? [];

    socialLinks.value = Object.fromEntries(socialPlatforms.value.map((p) => [p.name, '']));

    if (props.initialData) {
      fillFormFromInitialData(props.initialData);
    }
  } catch (err) {
    errorMessage.value = 'Failed to load required data.';
  } finally {
    pageLoading.value = false;
  }
});

watch(
  () => props.initialData,
  (val) => {
    if (val) fillFormFromInitialData(val);
  }
);

function onImageSelected(base64: string, file?: File) {
  image.value = base64;
  if (file) imageFile.value = file;
}

async function handleSubmit() {
  errorMessage.value = '';

  if (!name.value || !role.value || !contribution.value) {
    errorMessage.value = 'Please fill in all required fields.';
    return;
  }

  let processedContribution = contribution.value;

  const base64Matches = [...processedContribution.matchAll(/<img[^>]+src=["'](data:[^"']+)["'][^>]*>/gi)];

  for (const match of base64Matches) {
    const fullTag = match[0];
    const base64Data = match[1];

    const response = await fetch(base64Data);
    const blob = await response.blob();
    const file = new File([blob], `embedded-${Date.now()}.png`, { type: blob.type });

    const compressed = await compressImage(file, 1000, 0.8);
    const uploadRes = await uploadImage(compressed);
    const newUrl = `/api/common/images/${uploadRes.imageId}`;

    const updatedTag = fullTag.replace(base64Data, newUrl);
    processedContribution = processedContribution.replace(fullTag, updatedTag);
  }

  const payload: Founder = {
    name: name.value,
    role: role.value,
    contribution: processedContribution,
    races: selectedRaces.value,
    socialLinks: socialPlatforms.value
      .filter((p) => socialLinks.value[p.name])
      .map((p) => ({
        id: p.id,
        link: socialLinks.value[p.name],
      })),
  };

  if (imageFile.value) {
    const compressed = await compressImage(imageFile.value, 1000, 0.8);
    const imgRes = await uploadImage(compressed);
    payload.imageId = imgRes.imageId;
  }

  try {
    await props.onSubmit(payload);
  } catch (err: any) {
    console.error(err);
    errorMessage.value = err?.message || 'Failed to save founder.';
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!

  return (pageLoading.value)
    ? (_openBlock(), _createBlock(_component_v_progress_circular, {
        key: 0,
        indeterminate: "",
        color: "primary",
        class: "d-flex mx-auto my-6"
      }))
    : (_openBlock(), _createBlock(_component_v_form, {
        key: 1,
        onSubmit: _withModifiers(handleSubmit, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_text_field, {
            modelValue: name.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((name).value = $event)),
            label: "Name",
            outlined: "",
            required: ""
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_text_field, {
            modelValue: role.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((role).value = $event)),
            label: "Role",
            outlined: "",
            required: ""
          }, null, 8, ["modelValue"]),
          _createVNode(_component_v_select, {
            modelValue: selectedRaces.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedRaces).value = $event)),
            items: races.value,
            "item-title": "name",
            "item-value": "id",
            label: "Races",
            multiple: "",
            chips: "",
            outlined: ""
          }, null, 8, ["modelValue", "items"]),
          _createVNode(ImageCropper, {
            modelValue: image.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((image).value = $event)),
            class: "my-4",
            label: "Founder Image",
            onChange: onImageSelected,
            aspectRatio: 1
          }, null, 8, ["modelValue"]),
          _createVNode(RichTextEditor, {
            modelValue: contribution.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((contribution).value = $event)),
            embeddedImages: embeddedImages.value,
            "onUpdate:embeddedImages": _cache[5] || (_cache[5] = ($event: any) => ((embeddedImages).value = $event)),
            label: "Contribution"
          }, null, 8, ["modelValue", "embeddedImages"]),
          _cache[7] || (_cache[7] = _createElementVNode("div", { class: "text-h6 mb-2" }, "Social Links", -1)),
          _createVNode(_component_v_row, null, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(socialPlatforms.value, (platform) => {
                return (_openBlock(), _createBlock(_component_v_col, {
                  key: platform.id,
                  cols: "12",
                  sm: "6",
                  md: "4"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_text_field, {
                      modelValue: socialLinks.value[platform.name],
                      "onUpdate:modelValue": ($event: any) => ((socialLinks.value[platform.name]) = $event),
                      label: `${platform.name.charAt(0).toUpperCase() + platform.name.slice(1)} link`,
                      outlined: "",
                      clearable: ""
                    }, null, 8, ["modelValue", "onUpdate:modelValue", "label"])
                  ]),
                  _: 2
                }, 1024))
              }), 128))
            ]),
            _: 1
          }),
          (errorMessage.value)
            ? (_openBlock(), _createBlock(_component_v_alert, {
                key: 0,
                type: "error",
                variant: "tonal",
                class: "mt-2"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(errorMessage.value), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_v_btn, {
            loading: props.loading,
            type: "submit",
            color: "primary",
            block: "",
            class: "mt-4"
          }, {
            default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
              _createTextVNode(" Save Founder ", -1)
            ]))]),
            _: 1
          }, 8, ["loading"])
        ]),
        _: 1
      }))
}
}

})